import { useId, useRef, useState } from 'react'

import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { fr } from '@/i18n/fr'
import { cn } from '@/lib/utils'

export interface ComboboxItem {
  id: number
  name: string
}

interface Props<T extends ComboboxItem> {
  value: string
  items: T[]
  onChange: (text: string) => void
  /** Fired when a suggestion is chosen or a new name is confirmed. `item` is absent for a new one. */
  onCommit: (name: string, item?: T) => void
  placeholder?: string
  ariaLabel: string
  className?: string
  inputClassName?: string
  /** Extra key handling on the field itself, for callers with their own shortcuts. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  /** Rendered to the right of a suggestion — the ingredient list uses it for the default unit. */
  renderHint?: (item: T) => React.ReactNode
}

/**
 * A text field that suggests from an existing vocabulary without ever requiring one.
 *
 * Both vocabularies in this app are created-on-first-use — tags (F-13) and ingredient names
 * (F-14) — so anything typed here is valid whether or not it matches. The suggestion list is
 * an accelerator, never a gate: it renders inline beneath the real input rather than inside a
 * popover, so tapping the field never hands focus to a second input in an overlay, which on a
 * phone is the difference between typing and fighting the keyboard.
 */
export function Combobox<T extends ComboboxItem>({
  value,
  items,
  onChange,
  onCommit,
  placeholder,
  ariaLabel,
  className,
  inputClassName,
  onKeyDown,
  renderHint,
}: Props<T>) {
  const [open, setOpen] = useState(false)
  const listId = useId()
  const blurTimer = useRef<number | undefined>(undefined)

  const trimmed = value.trim()
  const isNew = trimmed !== '' && !items.some((item) => matches(item.name, trimmed))

  function commit(name: string, item?: T) {
    onCommit(name.trim(), item)
    setOpen(false)
  }

  return (
    <Command
      // Filtering is cmdk's, driven by the same string the input shows.
      className={cn('relative', className)}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault()
          event.stopPropagation()
          setOpen(false)
        }
      }}
    >
      <CommandInput
        value={value}
        onValueChange={(text) => {
          onChange(text)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          // Deferred so a tap on a suggestion, which blurs the input first, still lands.
          blurTimer.current = window.setTimeout(() => setOpen(false), 120)
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-controls={open ? listId : undefined}
        className={inputClassName}
      />

      {open && (
        <CommandList
          id={listId}
          onMouseDown={(event) => {
            // Keep the tap from blurring the input at all where the browser allows it; the
            // timer above covers the browsers where it still does.
            event.preventDefault()
            window.clearTimeout(blurTimer.current)
          }}
        >
          {!isNew && <CommandEmpty>{fr.common.noSuggestion}</CommandEmpty>}

          {isNew && (
            <CommandItem value={trimmed} onSelect={() => commit(trimmed)}>
              {fr.common.createNamed(trimmed)}
            </CommandItem>
          )}

          {items.map((item) => (
            <CommandItem key={item.id} value={item.name} onSelect={() => commit(item.name, item)}>
              <span className="flex-1 truncate">{item.name}</span>
              {renderHint?.(item)}
            </CommandItem>
          ))}
        </CommandList>
      )}
    </Command>
  )
}

/** Case- and accent-insensitive, so "Creme" does not offer to create a second "crème". */
function matches(candidate: string, typed: string): boolean {
  return normalise(candidate) === normalise(typed)
}

function normalise(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase('fr')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}
